// The team: every member with email, role and status. Owners and admins
// manage it; the server enforces who may change whom.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import type { components } from './generated/schema'

export type TeamMember = components['schemas']['TeamMember']
export type TeamInvite = components['schemas']['TeamInvite']
export type TeamMemberUpdate = components['schemas']['TeamMemberUpdate']
export type MemberStatus = components['schemas']['MemberStatus']

const teamKey = ['team'] as const

export function useTeam() {
  return useQuery({
    queryKey: teamKey,
    queryFn: async (): Promise<TeamMember[]> => unwrap(await api.GET('/team')),
  })
}

// A team change alters the assign picker too.
function useRefreshTeam() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: teamKey })
    void qc.invalidateQueries({ queryKey: ['teammates'] })
  }
}

export function useInviteTeamMember() {
  const refresh = useRefreshTeam()
  return useMutation({
    mutationFn: async (body: TeamInvite): Promise<TeamMember> => unwrap(await api.POST('/team', { body })),
    onSuccess: refresh,
  })
}

export function useUpdateTeamMember(userId: string) {
  const refresh = useRefreshTeam()
  return useMutation({
    mutationFn: async (body: TeamMemberUpdate): Promise<TeamMember> =>
      unwrap(await api.PATCH('/team/{userId}', { params: { path: { userId } }, body })),
    onSuccess: refresh,
  })
}
